<template>
  <dpc-form-communicator v-model:value="internalValue" :name="name">
    <div
      v-bind="$attrs"
      class="input-text"
      :class="{
        'input-text--full-width': fullWidth,
        'input-text--is-disabled': disabled,
      }"
    >
      <label
        v-if="!!$slots.label || label"
        :class="{ 'a-visually-hidden': labelHidden }"
        :for="id"
        class="input-text__label"
      >
        <slot name="label" />{{ label }}{{ required ? ' *' : '' }}
      </label>
      <slot />
      <input
        v-bind="{
          min: min,
          max: max,
          ...$makeFieldUpdateBinding({
            classListObject: {
              'input-text__input--is-disabled': disabled,
            },
          }),
        }"
        :id="id"
        ref="input"
        v-focus-on-page-load="{ active: focusOnPageLoad }"
        :placeholder="placeholder"
        :value="hasFocus ? inputValue : internalValue"
        :name="name"
        :type="type"
        :required="required"
        :aria-required="required ? 'true' : undefined"
        :disabled="disabled"
        class="input-text__input"
        @click="selectAll"
        @keydown="onKeydown"
        @change="onChange"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <slot name="after-input" />
      <dpc-form-error-portal :name="name" />
    </div>
  </dpc-form-communicator>
</template>

<script>
import {
  InputIdProvider,
  FieldUpdateProvider,
} from '@components/organisms/form/form.mixin.js';

import debounce from 'lodash/debounce';
import { inject } from 'vue';

const UPDATE_DEBOUNCE_DELAY = 750;

function isNumberEvent(event) {
  var key = event.keyCode || event.which;

  if (
    (!event.shiftKey &&
      !event.altKey &&
      !event.ctrlKey &&
      // numbers
      key >= 48 &&
      key <= 57) ||
    // Numeric keypad
    (key >= 96 && key <= 105) ||
    // Backspace and Tab and Enter
    key == 8 ||
    key == 9 ||
    key == 13 ||
    // Home and End
    key == 35 ||
    key == 36 ||
    // left and right arrows
    key == 37 ||
    key == 39 ||
    // Del and Ins
    key == 46 ||
    key == 45
  ) {
    // input is VALID
    return true;
  }

  return false;
}

export default {
  inheritAttrs: false,
  compatConfig: { INSTANCE_ATTRS_CLASS_STYLE: false },

  mixins: [
    InputIdProvider,
    FieldUpdateProvider({ baseClass: 'input-text__input' }),
  ],

  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
    },
    type: {
      type: String,
      default: 'text',
    },
    value: {
      type: String,
      default: '',
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    labelHidden: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectAllOnClick: {
      type: Boolean,
      default: false,
    },
    focusOnPageLoad: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:value'],

  setup() {
    const submit = inject('form/submit');
    return { submit };
  },

  data() {
    return {
      hasFocus: false,
      internalValue: this.value,
      inputValue: JSON.parse(JSON.stringify(this.value)),
    };
  },

  watch: {
    value(value) {
      this.internalValue = value;
    },
    internalValue(value) {
      if (this.value === value) return;
      this.$emit('update:value', value);
    },
  },

  mounted() {
    this.debouncedUpdateInternalValue = debounce(
      this.updateInternalValue,
      UPDATE_DEBOUNCE_DELAY
    );
  },
  methods: {
    onChange(event) {
      const value = event.target.value;
      const correctedValue = this.getCorrectedValue(value);
      this.updateValues(correctedValue);
    },
    onInput(event) {
      const value = event.target.value;
      this.updateValues(value);

      // do not correct value onInput if string is empty,
      // because else the ux would be kinda messy
      if (value === '') return;

      const correctedValue = this.getCorrectedValue(value);

      // do not correct if no correction is needed
      if (value === correctedValue) return;

      this.updateValues(correctedValue);
      event.preventDefault();
    },
    onKeydown(event) {
      if (this.type === 'number') {
        const valid = isNumberEvent(event);
        if (!valid) {
          event.preventDefault();
          return false;
        }
      }

      if (event.key === 'Enter') {
        this.forceUpdateInternalValue(this.inputValue);
        this.$nextTick(() => {
          this.submit();
        });
        event.preventDefault();
        return false;
      }
    },
    updateValues(value) {
      this.updateInputValue(value);
      this.debouncedUpdateInternalValue(value);
    },
    updateInternalValue(value) {
      if (this.internalValue === value) return;
      this.internalValue = value;
      this.$emit('update:value', value);
    },
    forceUpdateInternalValue(value) {
      // we need to update internalValue now, thus
      // cancel debounced updates
      this.debouncedUpdateInternalValue.cancel();
      this.updateInternalValue(value);
    },
    updateInputValue(value) {
      if (this.inputValue === value) return;
      this.inputValue = value;
      this.$refs.input.value = value;
    },
    getCorrectedValue(value) {
      if (this.type === 'number') {
        const integerValue = value.replaceAll(/[^\d]/g, '');

        if (this.max && Number(integerValue) > this.max) {
          return String(this.max);
        }

        if (this.min && Number(integerValue) < this.min) {
          return String(this.min);
        }

        return integerValue;
      }

      return value;
    },
    selectAll() {
      if (!this.selectAllOnClick) return;
      this.$refs.input.select();
    },
    onFocus() {
      this.hasFocus = true;
      this.updateInputValue(this.internalValue);
    },
    onBlur() {
      this.hasFocus = false;
      this.forceUpdateInternalValue(this.inputValue);
    },
  },
};
</script>
